import axios from './axios';
import { frontConfig } from '../config';
import { isBrowser } from '../utils/fragments/isBrowser';

/**
 * Funnel d'engagement forum (graphdebate) — émetteur client (B4_02).
 *
 * Émet les beacons funnel vers les handlers product-aware (B4_03) sur la page topic :
 *  - lecture qualifiée : dwell cumulé ≥3s sur la page topic (post d'origine compris) → /dwell?first=1
 *  - engagé-dwell      : dwell cumulé ≥10s → /dwell?engaged=1&engaged_first=1
 *  - engagé-interaction: scroll profond / pagination / vote → /has_engaged (markTopicEngaged)
 *
 * Garanties (cf. SPEC_funnel_forum.md / METRIQUES_REFONTE §2) :
 *  - one-shot par session sur chaque palier (qualifiedSent / engagedDwellSent / engagedSent) ;
 *  - dwell CUMULATIF (cycles entrée/sortie de viewport additionnés), envoyé en DELTAS pour ne
 *    pas double-compter total_dwell_time côté serveur (`$inc`) ;
 *  - le serveur résout le product via getProductByThread(thread) → product:'graphdebate'.
 *
 * Module-level (singleton) plutôt qu'un contexte React : markTopicEngaged() doit être appelable
 * depuis des composants profonds (vote, pagination) sans prop drilling. No-op tant qu'aucun
 * funnel n'est actif (hors page topic), donc sûr pour les usages non-forum des composants partagés.
 */

const QUALIFIED_MS = 3000;
const ENGAGED_DWELL_MS = 10000;
// Scroll profond = avoir dépassé ~1 hauteur d'écran (être descendu sous le post d'origine,
// vers la discussion). Heuristique volontairement simple, robuste sur topic long comme court.
const DEEP_SCROLL_VIEWPORT_FACTOR = 1;
const TICK_MS = 1000;

let active = false;
let threadId: string | null = null;
let startTime: number | null = null; // ms epoch du cycle visible courant, null si caché
let accumulatedDwell = 0; // ms visibles cumulés hors cycle courant
let lastSentDwell = 0; // ms déjà envoyés (pour calculer les deltas)
let qualifiedSent = false;
let engagedDwellSent = false;
let engagedSent = false;
let tick: ReturnType<typeof setInterval> | null = null;
let scrollEl: HTMLElement | null = null; // conteneur de scroll réel (#__next), pas window

const currentDwell = (): number =>
  accumulatedDwell + (startTime !== null ? Date.now() - startTime : 0);

const sendDwell = (params: Record<string, string | number>): void => {
  if (!threadId) return;
  const qs = new URLSearchParams(
    Object.entries(params).map(([k, v]) => [k, String(v)] as [string, string]),
  ).toString();
  // GET (les routes funnel sont en pub.get) ; échec silencieux (mesure best-effort).
  axios.get(`/api/pub/has_read_comments/thread/${threadId}/dwell?${qs}`).catch(() => undefined);
};

const checkDwellThresholds = (): void => {
  const dwell = currentDwell();
  if (!qualifiedSent && dwell >= QUALIFIED_MS) {
    qualifiedSent = true;
    const delta = dwell - lastSentDwell; // ≥3000 (premier envoi) → passe le garde-fou serveur
    lastSentDwell = dwell;
    sendDwell({ dwell_time: delta, first: 1 });
  }
  if (!engagedDwellSent && dwell >= ENGAGED_DWELL_MS) {
    engagedDwellSent = true;
    const delta = dwell - lastSentDwell;
    lastSentDwell = dwell;
    sendDwell({ dwell_time: delta, first: 0, engaged: 1, engaged_first: 1 });
  }
  if (qualifiedSent && engagedDwellSent && tick) {
    clearInterval(tick);
    tick = null;
  }
};

/**
 * Signal d'engagement actif (scroll profond / pagination / vote). One-shot par session.
 * No-op si aucun funnel topic n'est actif (sûr pour les composants partagés hors forum).
 */
export const markTopicEngaged = (): void => {
  if (!active || !threadId || engagedSent) return;
  engagedSent = true;
  axios.get(`/api/pub/has_engaged/thread/${threadId}`).catch(() => undefined);
};

const flushDwell = (): void => {
  // Page cachée/fermée : cumuler le cycle courant, franchir d'éventuels paliers (≥3s/≥10s)
  // — fiable sur mobile (bascule d'onglet/app, où pagehide est peu fiable) — puis envoyer le
  // delta restant pour compléter total_dwell_time.
  if (startTime !== null) {
    accumulatedDwell += Date.now() - startTime;
    startTime = null;
  }
  checkDwellThresholds();
  const delta = currentDwell() - lastSentDwell;
  if (delta > 0) {
    lastSentDwell = currentDwell();
    sendDwell({ dwell_time: delta, first: 0 });
  }
};

const onVisibility = (): void => {
  if (document.hidden) {
    flushDwell();
  } else if (startTime === null) {
    startTime = Date.now();
  }
};

const onScroll = (): void => {
  if (engagedSent || !scrollEl) return;
  // graphdebate scrolle sur #__next (overflow CSS), pas window : l'event scroll ne bulle pas
  // vers window et window.scrollY reste 0. On lit scrollTop/clientHeight de l'élément racine.
  if (scrollEl.scrollTop > scrollEl.clientHeight * DEEP_SCROLL_VIEWPORT_FACTOR) {
    markTopicEngaged();
  }
};

const onPageHide = (): void => flushDwell();

/** Démarre le suivi funnel pour un topic (page topic graphdebate). Client-only. */
export const startTopicFunnel = (id: string | null | undefined): void => {
  if (!isBrowser || !id) return;
  stopTopicFunnel(); // reset propre si un funnel tournait déjà (navigation entre topics)
  active = true;
  threadId = id;
  startTime = document.hidden ? null : Date.now();
  accumulatedDwell = 0;
  lastSentDwell = 0;
  qualifiedSent = false;
  engagedDwellSent = false;
  engagedSent = false;
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', onPageHide);
  // Scroll écouté sur le conteneur réel (#__next), pas window (cf. onScroll / scrollSections.tsx).
  scrollEl = document.querySelector<HTMLElement>(frontConfig.rootElementSelector);
  if (scrollEl) {
    scrollEl.addEventListener('scroll', onScroll, { passive: true });
  }
  tick = setInterval(checkDwellThresholds, TICK_MS);
};

/** Arrête le suivi et nettoie les listeners (démontage / changement de topic). */
export const stopTopicFunnel = (): void => {
  if (!isBrowser) return;
  if (tick) {
    clearInterval(tick);
    tick = null;
  }
  document.removeEventListener('visibilitychange', onVisibility);
  window.removeEventListener('pagehide', onPageHide);
  if (scrollEl) {
    scrollEl.removeEventListener('scroll', onScroll);
    scrollEl = null;
  }
  active = false;
  threadId = null;
  startTime = null;
  accumulatedDwell = 0;
  lastSentDwell = 0;
};
